import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";
import {
  FaUser,
  FaLock,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaKey,
  FaUserShield,
  FaAddressCard,
  FaCheckCircle,
  FaSave
} from "react-icons/fa";
import "../styles/ManageAccount.css";

const ManageAccount = () => {
  /* ================= STATES ================= */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    alternatePhone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    role: "",
    gender: "",
    dateOfBirth: "",
    bio: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Redesign Tab state
  const [activeTab, setActiveTab] = useState("profile"); // "profile" | "address" | "security"

  /* ================= FETCH PROFILE ================= */
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_PROFILE, {
        headers: {
          Authorization: localStorage.getItem("adminToken"),
        },
      });

      if (!response.ok) {
        localStorage.removeItem("adminToken");
        window.location.href = "/admin/login";
        return;
      }

      const data = await response.json();

      if (data) {
        setFormData((prev) => ({
          ...prev,
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          alternatePhone: data.alternatePhone || "",
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          country: data.country || "",
          pincode: data.pincode || "",
          role: data.role || "",
          gender: data.gender || "",
          dateOfBirth: data.dateOfBirth || "",
          bio: data.bio || "",
        }));
      } else {
        localStorage.removeItem("adminToken");
        window.location.href = "/admin/login";
      }
    } catch (error) {
      console.log(error);
    }
  };

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  /* ================= UPDATE PROFILE ================= */
  const handleUpdate = async (e) => {
    if (e) e.preventDefault();

    /* PASSWORD VALIDATION */
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      setSaveSuccess(false);

      const response = await fetch(API_ENDPOINTS.ADMIN_PROFILE, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("adminToken"),
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        alert("🎉 Profile Updated Successfully!");

        setFormData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
        
        fetchProfile();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.log(error);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-account-container">
      {/* BANNER SECTION */}
      <div className="account-banner">
        <div className="banner-content">
          <h1>👤 Manage Admin Account</h1>
          <p>Update personal credentials, account bio, city locations, and secure passwords here.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaUserShield className="icon" />
            <span>Role: <strong>{formData.role || "Admin"}</strong></span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & TAB WRAPPER */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            type="button"
            className={`tab-btn ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            <FaUser /> Profile Details
          </button>
          <button 
            type="button"
            className={`tab-btn ${activeTab === "address" ? "active" : ""}`}
            onClick={() => setActiveTab("address")}
          >
            <FaAddressCard /> Address & Location
          </button>
          <button 
            type="button"
            className={`tab-btn ${activeTab === "security" ? "active" : ""}`}
            onClick={() => setActiveTab("security")}
          >
            <FaLock /> Password & Security
          </button>
        </div>

        <div className="quick-action-buttons">
          <button 
            type="button"
            className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-small"></div> Updating...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        <form onSubmit={handleUpdate} className="account-form-grid">
          
          {/* TAB: PROFILE DETAILS */}
          {activeTab === "profile" && (
            <div className="panel-card fade-in">
              <div className="panel-card-header">
                <h3><FaUser className="title-icon" /> Personal Profile Details</h3>
                <p>Manage your basic account identity, email, phone, and role settings.</p>
              </div>

              <div className="panel-card-body fields-grid">
                <div className="form-group">
                  <label><FaUser className="input-field-icon" /> Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Full Name"
                  />
                </div>

                <div className="form-group">
                  <label><FaEnvelope className="input-field-icon" /> Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter Email"
                  />
                </div>

                <div className="form-group">
                  <label><FaPhoneAlt className="input-field-icon" /> Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter Phone Number"
                  />
                </div>

                <div className="form-group">
                  <label><FaUserShield className="input-field-icon" /> Admin Role</label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Date Of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth ? formData.dateOfBirth.split("T")[0] : ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Bio / Description</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Provide a short description about yourself..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB: ADDRESS & CONTACT */}
          {activeTab === "address" && (
            <div className="panel-card fade-in">
              <div className="panel-card-header">
                <h3><FaAddressCard className="title-icon" /> Location & Alternate Contacts</h3>
                <p>Manage your address details and backup contact phone numbers.</p>
              </div>

              <div className="panel-card-body fields-grid">
                <div className="form-group">
                  <label><FaPhoneAlt className="input-field-icon" /> Alternate Phone Number</label>
                  <input
                    type="text"
                    name="alternatePhone"
                    value={formData.alternatePhone}
                    onChange={handleChange}
                    placeholder="Alternate Phone Number"
                  />
                </div>

                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                  />
                </div>

                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                  />
                </div>

                <div className="form-group">
                  <label>Pincode / Postal Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="Pincode"
                  />
                </div>

                <div className="form-group full-width">
                  <label><FaMapMarkerAlt className="input-field-icon" /> Full Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter Full Address"
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB: PASSWORD & SECURITY */}
          {activeTab === "security" && (
            <div className="panel-card fade-in">
              <div className="panel-card-header">
                <h3><FaLock className="title-icon" /> Update Password Credentials</h3>
                <p>Change your secure login password here. Keep your login secure.</p>
              </div>

              <div className="panel-card-body fields-grid">
                <div className="form-group">
                  <label><FaKey className="input-field-icon" /> Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    placeholder="Enter Current Password"
                  />
                </div>

                <div className="form-group">
                  <label><FaKey className="input-field-icon" /> New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter New Password"
                  />
                </div>

                <div className="form-group">
                  <label><FaKey className="input-field-icon" /> Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm New Password"
                  />
                </div>
              </div>
            </div>
          )}

          {/* HIDDEN SUBMIT TO ALLOW FORM ENTER PRESS SUBMITTING */}
          <button type="submit" style={{ display: "none" }}></button>

        </form>
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to your admin profile will be lost.</span>
        </div>
        
        <button 
          className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleUpdate}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageAccount;
